"use client";

import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';

interface LightboxProps {
  images: { src: string; alt: string }[];
  currentIndex: number;
  onClose: () => void;
}

const Lightbox = ({ images, currentIndex: initialIndex, onClose }: LightboxProps) => {
  const [index, setIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  const currentImage = images[index];

  useEffect(() => {
    setMounted(true);
    previousFocus.current = document.activeElement as HTMLElement;
    
    // Prevent scroll and handle scrollbar jump
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      previousFocus.current?.focus();
    };
  }, []);

  const handleNavigate = useCallback((direction: 'next' | 'prev') => {
    if (direction === 'next') {
      setIndex((prev) => (prev + 1) % images.length);
    } else {
      setIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  }, [images.length]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') handleNavigate('next');
    if (e.key === 'ArrowLeft') handleNavigate('prev');
    
    // Basic focus trap - keep it on the modal content
    if (e.key === 'Tab') {
      // For simplicity in this single-button + arrows modal, we just keep focus on the close button or don't trap strictly
      // But we prevent it from going to the background
      e.preventDefault();
      closeButtonRef.current?.focus();
    }
  }, [onClose, handleNavigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!mounted) return null;

  const content = (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery viewer"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-bloom-charcoal/95 backdrop-blur-md"
        onClick={onClose}
      />
      
      <button
        ref={closeButtonRef}
        onClick={onClose}
        className="absolute top-6 right-6 z-[110] p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus-bloom"
        aria-label="Close image viewer"
      >
        <X size={24} aria-hidden="true" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); handleNavigate('prev'); }}
            className="absolute left-4 md:left-8 z-[110] p-3 md:p-4 rounded-full bg-white/5 text-white hover:bg-white/15 transition-colors focus-bloom"
            aria-label="Previous image"
          >
            <ChevronLeft size={32} aria-hidden="true" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleNavigate('next'); }}
            className="absolute right-4 md:right-8 z-[110] p-3 md:p-4 rounded-full bg-white/5 text-white hover:bg-white/15 transition-colors focus-bloom"
            aria-label="Next image"
          >
            <ChevronRight size={32} aria-hidden="true" />
          </button>
        </>
      )}

      <div className="relative z-[105] w-full max-w-5xl h-full flex items-center justify-center p-4 md:p-12 pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="relative max-w-full max-h-full shadow-2xl rounded-xl overflow-hidden pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-[80vh] aspect-video">
              <Image
                src={currentImage.src}
                alt={currentImage.alt}
                fill
                priority
                sizes="100vw"
                className="object-contain bg-bloom-linen/5"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <p className="font-sans text-sm text-white font-medium text-center">
                {currentImage.alt}
              </p>
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-white/50 text-center mt-2">
                PROJECT GALLERY &mdash; {index + 1} of {images.length}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default Lightbox;

